import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useSEO } from "@/hooks/useSEO";
import { CTALink } from "@/components/common/CTALink";
import { Reveal } from "@/components/common/Reveal";
import { SectionHeading } from "@/components/common/SectionHeading";
import { FAQ } from "@/components/blocks/FAQ";
import { CtaBand } from "@/components/blocks/CtaBand";
import { contactLink } from "@/data/site";
import { startPlanPayment } from "@/lib/razorpay";
import { WA_PLANS, WA_FEATURE_ROWS, WA_BILLING, META_RATE_CARD, WA_PRICING_FAQS } from "@/data/whatsapp";

const TALK = contactLink("WhatsApp Automation");

export default function WhatsAppPricing() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(WA_BILLING.default);
  const [processingKey, setProcessingKey] = useState(null);
  const [success, setSuccess] = useState(null);
  const periodMeta = WA_BILLING.periods.find((x) => x.key === period) || WA_BILLING.periods[0];
  const periodLabel = periodMeta.label.toLowerCase();

  const handlePay = async (plan) => {
    if (processingKey) return;
    setProcessingKey(plan.key);
    try {
      const result = await startPlanPayment({
        planKey: plan.key,
        period,
        businessName: "E Telecom",
        description: `${plan.name} — WhatsApp Automation (${periodMeta.label})`,
      });
      if (result.status === "success") {
        setSuccess({
          planName: result.order?.plan_name || plan.name,
          periodLabel: result.order?.period_label || periodMeta.label,
          amount: result.order?.amount_inr || plan.prices[period].amount,
          paymentId: result.payment_id,
        });
      }
    } catch (e) {
      if (e.code === "disabled") {
        toast.info("Online payment is being set up. Please share your details and our team will assist you.");
        navigate(contactLink("WhatsApp Automation", `${plan.name} — WhatsApp Automation (${periodMeta.label})`));
      } else if (e.code === "dismissed") {
        // user closed checkout — no error toast
      } else {
        toast.error(e.message || "Payment could not be completed.");
      }
    } finally {
      setProcessingKey(null);
    }
  };

  useSEO({
    title: "WhatsApp Automation Pricing & Plans | E Telecom",
    description:
      "Compare E Telecom WhatsApp Automation plans — Starter, Business and Advanced. Transparent platform pricing with zero markup on Meta message charges.",
    path: "/whatsapp-automation/pricing",
  });

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden et-hero-wash">
        <div className="absolute inset-0 et-dot-grid opacity-[0.35] pointer-events-none" />
        <div className="et-container relative py-14 lg:py-16 text-center">
          <Reveal>
            <span className="et-eyebrow"><Icons.MessageSquareText className="h-3.5 w-3.5" /> WhatsApp Automation Pricing</span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mt-5">Simple Plans. Powerful WhatsApp Automation.</h1>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-base leading-relaxed">Choose the plan that fits your team. Every plan runs on the official WhatsApp Business Platform — with zero E Telecom markup on Meta message charges.</p>
          </Reveal>
        </div>
      </section>

      {/* PLAN CARDS */}
      <section className="pb-6 pt-2" data-testid="wa-pricing-plans">
        <div className="et-container">
          {/* Billing period toggle */}
          <Reveal className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1 shadow-sm" role="tablist" aria-label="Billing period" data-testid="wa-billing-toggle">
              {WA_BILLING.periods.map((per) => (
                <button
                  key={per.key}
                  role="tab"
                  aria-selected={period === per.key}
                  onClick={() => setPeriod(per.key)}
                  data-testid={`wa-billing-${per.key}`}
                  className={`relative inline-flex items-center gap-1.5 rounded-full px-4 sm:px-5 py-2 text-sm font-semibold transition-colors ${period === per.key ? "bg-[var(--et-primary)] text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                >
                  {per.label}
                  {per.badge && (
                    <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${period === per.key ? "bg-white/20 text-white" : "bg-green-50 text-green-600"}`}>{per.badge}</span>
                  )}
                </button>
              ))}
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-3 gap-6 items-stretch">
            {WA_PLANS.map((p, i) => {
              const pr = p.prices[period];
              return (
              <Reveal key={p.key} delay={i * 0.06} className="h-full">
                <div className={`relative h-full rounded-2xl p-7 flex flex-col ${p.recommended ? "bg-[var(--et-navy)] text-white shadow-[0_20px_60px_rgba(2,6,23,0.28)] lg:-mt-2" : "et-card"}`}>
                  {p.recommended && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[var(--et-accent)] px-3 py-1 text-[11px] font-bold text-slate-900">Recommended</span>}
                  <h3 className={`font-display text-xl font-bold ${p.recommended ? "text-white" : "text-slate-900"}`}>{p.name}</h3>
                  <p className={`mt-1.5 text-sm min-h-[40px] ${p.recommended ? "text-slate-300" : "text-slate-500"}`}>{p.desc}</p>

                  <div className="mt-5 flex items-baseline gap-1" data-testid={`wa-price-${p.key}`}>
                    <span className={`font-display text-4xl font-bold ${p.recommended ? "text-white" : "text-slate-900"}`}>₹{pr.amount}</span>
                    <span className={`text-sm ${p.recommended ? "text-slate-300" : "text-slate-500"}`}>{pr.suffix}</span>
                  </div>
                  <div className="mt-1.5 h-5">
                    {pr.discount && (
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-bold ${p.recommended ? "bg-white/15 text-[var(--et-accent)]" : "bg-green-50 text-green-600"}`}>{pr.discount} vs monthly</span>
                    )}
                  </div>

                  <ul className="mt-5 space-y-2.5 flex-1">
                    {p.summary.map((f) => (
                      <li key={f} className={`flex items-start gap-2 text-sm ${p.recommended ? "text-slate-200" : "text-slate-600"}`}>
                        <Icons.Check className={`h-4 w-4 mt-0.5 shrink-0 ${p.recommended ? "text-[var(--et-accent)]" : "text-[var(--et-primary)]"}`} /> {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={() => handlePay(p)}
                    disabled={processingKey === p.key}
                    data-testid={`wa-pay-${p.key}`}
                    className={`mt-7 w-full inline-flex items-center justify-center gap-2 rounded-xl h-12 px-6 text-sm font-semibold transition-colors disabled:opacity-70 disabled:cursor-not-allowed ${p.recommended ? "bg-[var(--et-accent)] text-slate-900 hover:brightness-95" : "bg-[var(--et-primary)] text-white hover:bg-[var(--et-primary-hover)]"}`}
                  >
                    {processingKey === p.key ? (
                      <><Icons.Loader2 className="h-4 w-4 animate-spin" /> Processing…</>
                    ) : (
                      <><Icons.CreditCard className="h-4 w-4" /> Pay Now — ₹{pr.amount}</>
                    )}
                  </button>
                </div>
              </Reveal>
              );
            })}
          </div>
          <Reveal>
            <p className="mt-6 text-center text-xs text-slate-400 max-w-2xl mx-auto">Displayed prices are E Telecom platform plan fees, billed {periodLabel} ({WA_BILLING.periods.map((x) => x.label.toLowerCase()).join(", ")} options available). Meta WhatsApp message charges are billed separately at official Meta rates with zero E Telecom markup.</p>
          </Reveal>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="et-section">
        <div className="et-container">
          <SectionHeading eyebrow="Compare Plans" title="Compare WhatsApp Automation Plans" subtitle="A clear breakdown of what's included in each plan." />

          {/* Desktop table */}
          <Reveal className="mt-10 hidden lg:block">
            <div className="et-card overflow-hidden">
              <table className="w-full text-left" data-testid="wa-compare-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-6 py-4 text-sm font-semibold text-slate-500">Feature</th>
                    {WA_PLANS.map((p) => (
                      <th key={p.key} className={`px-6 py-4 text-center ${p.recommended ? "bg-[rgba(37,99,235,0.04)]" : ""}`}>
                        <span className="font-display text-base font-bold text-slate-900">{p.name}</span>
                        <span className="block text-xs font-normal text-slate-400">₹{p.prices[period].amount}{p.prices[period].suffix}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {WA_FEATURE_ROWS.map((row) => (
                    <tr key={row.key} className="hover:bg-slate-50/50">
                      <td className="px-6 py-3.5 text-sm font-medium text-slate-700">{row.label}</td>
                      {WA_PLANS.map((p) => (
                        <td key={p.key} className={`px-6 py-3.5 text-center ${p.recommended ? "bg-[rgba(37,99,235,0.03)]" : ""}`}>
                          <CellValue value={p.features[row.key]} type={row.type} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          {/* Mobile: plan tabs */}
          <Reveal className="mt-8 lg:hidden">
            <Tabs defaultValue="business" className="w-full">
              <TabsList className="grid grid-cols-3 w-full">
                {WA_PLANS.map((p) => (
                  <TabsTrigger key={p.key} value={p.key} className="text-xs data-[state=active]:text-[var(--et-primary)]">{p.name}</TabsTrigger>
                ))}
              </TabsList>
              {WA_PLANS.map((p) => (
                <TabsContent key={p.key} value={p.key} className="mt-4">
                  <div className="et-card overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 flex items-center justify-between">
                      <span className="font-display font-bold text-slate-900">{p.name}</span>
                      <span className="text-sm font-semibold text-[var(--et-primary)]">₹{p.prices[period].amount}{p.prices[period].suffix}</span>
                    </div>
                    <ul className="divide-y divide-slate-100">
                      {WA_FEATURE_ROWS.map((row) => (
                        <li key={row.key} className="flex items-center justify-between px-4 py-3 text-sm">
                          <span className="text-slate-600">{row.label}</span>
                          <CellValue value={p.features[row.key]} type={row.type} />
                        </li>
                      ))}
                    </ul>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </Reveal>
        </div>
      </section>

      {/* ZERO MARKUP EXPLANATION */}
      <section className="et-section bg-white">
        <div className="et-container">
          <SectionHeading eyebrow="Transparent Pricing" title="Your Message Volume Shouldn't Increase Our Markup" subtitle="Your total WhatsApp cost is simple and transparent — a fixed platform fee plus official Meta charges, with nothing added on top." />
          <Reveal className="mt-12 max-w-4xl mx-auto">
            <div className="et-card p-6 sm:p-10">
              <div className="flex flex-col lg:flex-row items-stretch justify-center gap-4">
                <CostNode icon="Server" title="E Telecom Platform Fee" sub="Your chosen plan" />
                <Operator symbol="+" />
                <CostNode icon="MessageSquareText" title="Official Meta Charges" sub="Billed at Meta rates" />
                <Operator symbol="=" />
                <CostNode icon="Wallet" title="Your WhatsApp Cost" sub="Simple & predictable" tone="primary" />
              </div>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 rounded-2xl bg-green-50 border border-green-100 px-6 py-5">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white"><Icons.BadgeCheck className="h-6 w-6" /></span>
                <div className="text-center sm:text-left">
                  <div className="text-sm text-slate-600">E Telecom markup on Meta message charges</div>
                  <div className="font-display text-3xl font-bold text-green-700">₹0</div>
                </div>
                <span className="sm:ml-auto text-sm font-semibold text-green-700">Pay Official Meta Rates — No E Telecom Markup</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* META CHARGES */}
      <section className="et-section">
        <div className="et-container">
          <SectionHeading eyebrow="Meta WhatsApp Message Charges" title="Platform Fees and Meta Charges Are Separate" subtitle="Meta determines WhatsApp Business Platform messaging charges based on message category, destination and its prevailing pricing policies. Where billing runs on direct Meta rates, you pay Meta's applicable charges separately — E Telecom does not add a markup." />
          <Reveal className="mt-5 flex justify-center">
            <div className="inline-flex flex-wrap items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-medium text-slate-500">
              <Icons.MapPin className="h-3.5 w-3.5 text-[var(--et-primary)]" /> {META_RATE_CARD.country}
              <span className="text-slate-300">•</span>
              {META_RATE_CARD.rateType}
              <span className="text-slate-300">•</span>
              Effective {META_RATE_CARD.updated}
            </div>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {META_RATE_CARD.categories.map((c, i) => {
              const Icon = Icons[c.icon] || Icons.Tag;
              return (
                <Reveal key={c.name} delay={(i % 4) * 0.05}>
                  <div className="et-card et-card-hover h-full p-6">
                    <span className="et-icon-box"><Icon className="h-5 w-5" /></span>
                    <h3 className="font-display text-base font-semibold text-slate-900 mt-4">{c.name}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                    {c.rate ? (
                      <div className="mt-4 flex items-baseline gap-1.5 border-t border-slate-100 pt-3">
                        <span className="font-display text-xl font-bold text-slate-900">{c.rate}</span>
                        <span className="text-xs text-slate-400">/ message</span>
                      </div>
                    ) : (
                      <p className="mt-3 text-xs font-medium text-slate-400">Charged at Meta's current rates</p>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="mt-8">
            <div className="et-card p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1">
                <p className="text-sm text-slate-600 leading-relaxed">
                  <Icons.Info className="inline h-4 w-4 text-[var(--et-primary)] mr-1 align-[-2px]" />
                  Meta pricing is determined by Meta and may change from time to time. Applicable charges depend on message category, destination and Meta's prevailing pricing policies.
                  {META_RATE_CARD.updated && <span className="ml-1 font-medium text-slate-500">Rate card last updated: {META_RATE_CARD.updated}.</span>}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <a href={META_RATE_CARD.referenceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 h-11 text-sm font-semibold text-slate-900 hover:bg-slate-50 transition-colors">
                  <Icons.ExternalLink className="h-4 w-4" /> View Latest Meta Rate Card
                </a>
                {META_RATE_CARD.docUrl ? (
                  <a href={META_RATE_CARD.docUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--et-primary)] px-5 h-11 text-sm font-semibold text-white hover:bg-[var(--et-primary-hover)] transition-colors">
                    <Icons.Download className="h-4 w-4" /> Download Rate Card
                  </a>
                ) : (
                  <span className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-5 h-11 text-sm font-semibold text-slate-400 cursor-not-allowed" title="Available once a current rate card is published">
                    <Icons.Download className="h-4 w-4" /> Download (soon)
                  </span>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <FAQ items={WA_PRICING_FAQS} title="WhatsApp Pricing FAQs" subtitle="Common questions about plans, Meta charges and onboarding." />

      <CtaBand
        title="Not sure which plan fits?"
        subtitle="Tell us your team size and message volume — we'll recommend the right WhatsApp Automation plan."
        primary={{ to: TALK, label: "Talk to Sales" }}
        secondary={{ to: "/whatsapp-automation#features", label: "Explore Features" }}
      />

      {/* Payment success dialog */}
      <Dialog open={!!success} onOpenChange={(o) => !o && setSuccess(null)}>
        <DialogContent className="sm:max-w-md" data-testid="payment-success-dialog">
          <div className="text-center py-2">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50">
              <Icons.CheckCircle2 className="h-9 w-9 text-green-600" />
            </span>
            <h3 className="mt-5 font-display text-2xl font-bold text-slate-900">Payment Successful!</h3>
            <p className="mt-2 text-sm text-slate-600">Thank you for choosing E Telecom. Our team will reach out shortly to begin your onboarding.</p>
            {success && (
              <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-left text-sm">
                <Row label="Plan" value={`${success.planName} — WhatsApp Automation`} />
                <Row label="Billing" value={success.periodLabel} />
                <Row label="Amount paid" value={`₹${success.amount}`} />
                {success.paymentId && <Row label="Payment ID" value={success.paymentId} mono />}
              </div>
            )}
            <button
              onClick={() => setSuccess(null)}
              data-testid="payment-success-close"
              className="mt-6 w-full inline-flex items-center justify-center rounded-xl h-11 px-6 text-sm font-semibold bg-[var(--et-primary)] text-white hover:bg-[var(--et-primary-hover)] transition-colors"
            >
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Row = ({ label, value, mono = false }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <span className="text-slate-500">{label}</span>
    <span className={`font-semibold text-slate-900 text-right ${mono ? "font-mono text-xs break-all" : ""}`}>{value}</span>
  </div>
);

const CellValue = ({ value, type }) => {
  if (type === "bool") {
    return value ? (
      <Icons.Check className="inline h-5 w-5 text-[#16A34A]" />
    ) : (
      <Icons.X className="inline h-4 w-4 text-slate-300" />
    );
  }
  return <span className="text-sm font-medium text-slate-800">{value}</span>;
};

const CostNode = ({ icon, title, sub, tone = "default" }) => {
  const Icon = Icons[icon] || Icons.Circle;
  return (
    <div className={`flex-1 rounded-2xl border px-4 py-5 text-center ${tone === "primary" ? "bg-[var(--et-navy)] border-transparent text-white" : "bg-white border-slate-200"}`}>
      <span className={`mx-auto flex h-11 w-11 items-center justify-center rounded-xl ${tone === "primary" ? "bg-white/10 text-[var(--et-accent)]" : "bg-[rgba(37,99,235,0.1)] text-[var(--et-primary)]"}`}><Icon className="h-5 w-5" /></span>
      <div className={`mt-3 text-sm font-semibold ${tone === "primary" ? "text-white" : "text-slate-900"}`}>{title}</div>
      <div className={`text-xs ${tone === "primary" ? "text-slate-300" : "text-slate-500"}`}>{sub}</div>
    </div>
  );
};

const Operator = ({ symbol }) => (
  <div className="flex items-center justify-center">
    <span className="font-display text-2xl font-bold text-slate-300">{symbol}</span>
  </div>
);
